import { IconTimes } from '@maia/icons';
import { Typography } from '@maia/ui';
import { Button } from 'react-aria-components';
import { useDocumentPreviewStore } from './../../../stores/document-preview.store';
import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useOverlay, FocusScope } from 'react-aria';

export const DocumentPreview = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { state, close } = useDocumentPreviewStore();

  const { overlayProps } = useOverlay(
    {
      isOpen: !!state?.isOpen,
      onClose: close,
      isDismissable: true,
    },
    overlayRef,
  );

  const portalRoot =
    typeof window !== 'undefined'
      ? document.getElementById('document-portal')
      : null;

  useEffect(() => {
    if (iframeRef.current && state?.isOpen && state.info?.type === 'email') {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        const reset = `
        <style>
          	html, body { margin: 0; padding: 0; }
        </style>
		<script>
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape') {
					window.parent.postMessage({ type: 'iframe-escape' }, '*');
				}
			});
		</script>
      `;

        doc.open();
        doc.write(`${reset}${state.info.data}`);
        doc.close();
      }
    }

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'iframe-escape') {
        close();
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [state?.info?.data, state?.info?.type, state?.isOpen]);

  if (!portalRoot || !state?.isOpen) return null;

  return createPortal(
    <div
      {...overlayProps}
      ref={overlayRef}
      className="fixed inset-0 z-50 animate-document-preview-background"
      role="dialog"
      aria-modal="true"
    >
      <FocusScope contain restoreFocus autoFocus>
        <div className="relative w-full h-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="min-h-[96px] bg-grayscale0 flex items-center justify-between p-32 z-10">
            <div className="flex items-center gap-24">
              <Button className="fill-info300" onPress={close}>
                <IconTimes className="size-24" />
              </Button>
              <Typography
                as="h2"
                variant="display6"
                weight="medium"
                className="text-primaryMain"
              >
                {state.info?.title}
              </Typography>
            </div>
          </div>

          {/* Iframe container */}
          <div className="w-full h-full flex justify-center z-0">
            <div className="max-w-6xl w-full overflow-auto bg-grayscale50 opacity-0 animate-document-preview-content delay-100">
              <iframe
                ref={iframeRef}
                sandbox="allow-same-origin allow-scripts allow-popups"
                className="w-full h-full"
                title="Email content"
              />
            </div>
          </div>
        </div>
      </FocusScope>
    </div>,
    portalRoot,
  );
};
